// Plans and what is free (shared by the browser and the server; no server imports here).
// Free for everyone: इकाई ०–२, the present tense in the Grammar Lab (every form), the first FREE_SETS sets of every
// practice game, आज का अभ्यास, the dictionary, and basic progress for every account: XP, levels, the daily streak and
// badges. Premium charges for depth: every unit, story, practice set and Grammar Lab page, the unit tests after the
// free units, the weekly leaderboard, the Bhashini voice and early access (lessons and stories a teacher has verified
// but not yet published for everyone).
// Payments are not connected yet: a learner requests a plan and an admin grants it in /admin/plans.

import type { L } from '@/prakrit/lib/lessons/types'

export type PlanId = 'sanskrit' | 'prakrit' | 'both'

export type BillingPeriod = 'month' | 'year'

export interface PlanInfo {
    id: PlanId
    name: L
    line: L
    /** Price in rupees per period. */
    prices: Partial<Record<BillingPeriod, number>>
    /** Opens the paid parts of this Prakrit course. */
    prakrit: boolean
    /** Opens the Sanskrit course (sanskrit.modulify.website). */
    sanskrit: boolean
    featured?: boolean
}

export const PLANS: PlanInfo[] = [
    {
        id: 'sanskrit',
        name: { hi: 'केवल संस्कृत', en: 'Sanskrit only' },
        line: { hi: 'संस्कृत पाठ्यक्रम पूरा', en: 'The full Sanskrit course' },
        prices: { month: 299 },
        prakrit: false,
        sanskrit: true
    },
    {
        id: 'prakrit',
        name: { hi: 'केवल प्राकृत', en: 'Prakrit only' },
        line: { hi: 'यह प्राकृत पाठ्यक्रम पूरा', en: 'This full Prakrit course' },
        prices: { month: 499, year: 5000 },
        prakrit: true,
        sanskrit: false
    },
    {
        id: 'both',
        name: { hi: 'संस्कृत + प्राकृत', en: 'Sanskrit + Prakrit' },
        line: { hi: 'दोनों पाठ्यक्रम एक साथ', en: 'Both courses together' },
        prices: { month: 599, year: 6000 },
        prakrit: true,
        sanskrit: true,
        featured: true
    }
]

export const PLAN_BY_ID: Record<PlanId, PlanInfo> = Object.fromEntries(PLANS.map(p => [p.id, p])) as Record<PlanId, PlanInfo>

export const isPlanId = (value: unknown): value is PlanId => typeof value === 'string' && value in PLAN_BY_ID

export const isBillingPeriod = (value: unknown): value is BillingPeriod => value === 'month' || value === 'year'

/** The periods a plan can be bought for. */
export const periodsOf = (plan: PlanId): BillingPeriod[] => (Object.keys(PLAN_BY_ID[plan].prices) as BillingPeriod[])

export const PERIOD_LABEL: Record<BillingPeriod, L> = {
    month: { hi: 'प्रति माह', en: 'per month' },
    year: { hi: 'प्रति वर्ष', en: 'per year' }
}

/** "₹6,000" */
export const rupees = (amount: number): string => `₹${amount.toLocaleString('en-IN')}`

/** What the yearly price saves over twelve months of the monthly one (0 when there is no yearly price). */
export const yearlySaving = (plan: PlanId): number => {
    const { month, year } = PLAN_BY_ID[plan].prices

    return month && year ? month * 12 - year : 0
}

/** Days of full access a new account gets, once. */
/** Free trials are switched off; kept at 0 for older code that still reads it. */
export const TRIAL_DAYS = 0

/** Units numbered below this are free (इकाई ०, १, २). */
export const FREE_UNITS = 3

/** Stories and dialogues for after these units are free (none: every story is Premium). */
export const FREE_STORY_LEVEL = -1

/** The only tense open in the Grammar Lab without Premium (all its forms). */
export const FREE_TENSE = 'vartaman'

/** Grammar Lab pages open to everyone; every other one needs Premium. */
export const FREE_GRAMMAR_PATHS = ['/grammar', '/grammar/kriya', '/grammar/purush-vachan', '/grammar/kal', '/grammar/kal/vartaman']

export const grammarPathIsFree = (href: string): boolean => FREE_GRAMMAR_PATHS.includes(href.split(/[?#]/)[0].replace(/\/$/, '') || '/')

/** The first sets of every practice game are free. */
export const FREE_SETS = 3

/** What one visitor may open. Staff (editors, reviewers, admins) always have full access. */
export interface Access {
    signedIn: boolean
    /** Prakrit Premium: every unit, story and practice set, the full progress page and early access (same as `prakrit`). */
    full: boolean
    /** Opens Prakrit Premium (staff, a Prakrit or Sanskrit + Prakrit plan, or the trial). */
    prakrit: boolean
    /** Opens Sanskrit Premium under /sanskrit (staff, a Sanskrit or Sanskrit + Prakrit plan, or the trial). */
    sanskrit: boolean
    source: 'staff' | 'plan' | 'trial' | 'free'
    plan: PlanId | 'trial' | null
    /** When the plan or trial ends (ISO), null for staff and free. */
    endsAt: string | null
    /** The account has never had a trial. */
    trialAvailable: boolean
    /** A plan request is waiting for an admin. */
    pendingRequest: { plan: PlanId, period: BillingPeriod } | null
    /**
     * The plans could not be checked just now (a database outage). Access stays closed (nothing paid opens without a
     * check), but pages say "we cannot confirm your Premium right now" instead of offering to sell it again.
     */
    unavailable?: boolean
}

export const GUEST_ACCESS: Access = {
    signedIn: false,
    full: false,
    prakrit: false,
    sanskrit: false,
    source: 'free',
    plan: null,
    endsAt: null,
    trialAvailable: false,
    pendingRequest: null
}

/** A lesson's unit number from its id (u7-… → 7). */
const unitNumberOf = (lessonId: string): number => {
    const match = /^u(\d+)-/.exec(lessonId)

    return match ? Number(match[1]) : Number.POSITIVE_INFINITY
}

export const lessonIsFree = (lessonId: string): boolean => unitNumberOf(lessonId) < FREE_UNITS

export const unitIsFree = (unitNumber: number): boolean => unitNumber < FREE_UNITS

export const storyIsFree = (level: number): boolean => level <= FREE_STORY_LEVEL

export const setIsFree = (set: number): boolean => set <= FREE_SETS

/** Whole days left until `endsAt` (at least 1 while it has not passed). */
export const daysLeft = (endsAt: string | null, now = Date.now()): number => {
    if (!endsAt) return 0

    const ms = Date.parse(endsAt) - now

    return ms > 0 ? Math.max(1, Math.ceil(ms / 86400000)) : 0
}

/** The Sanskrit course, part of this site since the Suvaani merge. */
export const SANSKRIT_SITE = '/sanskrit'

export type Course = 'prakrit' | 'sanskrit'

export const COURSE_HOME: Record<Course, string> = { prakrit: '/prakrit', sanskrit: '/sanskrit' }

/** Sanskrit lessons up to this number are free: levels 0 and 1 (l01 … l09, the script, greetings, objects and pronouns), plus l15, the first sentence. */
export const SANSKRIT_FREE_LESSONS = 9

/** Whether a Sanskrit lesson id (l07-…) is in the free part. */
export const sanskritLessonIsFree = (lessonId: string): boolean => {
    if (lessonId === 'l15-first-sentence') return true

    const match = /^l(\d+)/.exec(lessonId)

    return match ? Number(match[1]) <= SANSKRIT_FREE_LESSONS : false
}

/** Sanskrit practice games open without Premium (the daily review is always free). */
export const SANSKRIT_FREE_GAMES = ['akshara-nirman', 'dhatu-challenge', 'shravan']

/** Sanskrit Grammar Lab pages open without Premium: the hub, the person grid, the present tense in the dhātu lab. */
export const SANSKRIT_FREE_GRAMMAR = ['/sanskrit/grammar', '/sanskrit/grammar/purusha-vachana', '/sanskrit/grammar/dhatu']
